import { useI18n, type CommonKey } from '@hive/i18n';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';
import { cx } from './layout';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type=hidden]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true');
}

/**
 * Foco preso numa gaveta (detalhe, painel de contexto): ao ligar, o foco vai para a própria gaveta (lê o título);
 * Tab e Shift+Tab giram dentro dela; ao desligar, o foco volta a quem estava com ele (a linha da lista, o botão).
 */
export function useFocusTrap(ref: { current: HTMLElement | null }, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !el) return;
      const f = focusables(el);
      if (!f.length) {
        e.preventDefault();
        return;
      }
      const first = f[0]!;
      const last = f[f.length - 1]!;
      if (e.shiftKey && (document.activeElement === first || document.activeElement === el)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    el?.addEventListener('keydown', onKey);
    return () => {
      el?.removeEventListener('keydown', onKey);
      // só devolve quando o foco ainda está na gaveta (ou se perdeu com ela); quem clicou noutro lugar fica onde clicou
      const now = document.activeElement;
      const lost = !now || now === document.body || !!el?.contains(now);
      if (lost && prev && prev !== document.body && document.contains(prev)) prev.focus({ preventScroll: true });
    };
  }, [active, ref]);
}

// ---------------------------------------------------------------- modal

/** Modais abertos agora (um modal pode abrir outro, como a confirmação de sair do assistente). */
let openModals = 0;

/**
 * Diálogo modal acessível: foco preso dentro, Esc fecha, o foco volta para quem abriu,
 * o fundo fica inerte para leitores de tela.
 */
export function Modal({
  title,
  onClose,
  children,
  foot,
  size,
  initialFocus,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  foot?: ReactNode;
  size?: 'sm' | 'lg' | 'xl';
  /** Seletor do elemento que recebe o foco ao abrir (padrão: primeiro campo). */
  initialFocus?: string;
}) {
  const { t } = useI18n<CommonKey>();
  const id = useId();
  const box = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const el = box.current;
    const timer = window.setTimeout(() => {
      if (!el) return;
      const target =
        (initialFocus ? el.querySelector<HTMLElement>(initialFocus) : null) ??
        el.querySelector<HTMLElement>('.modal-body [autofocus]') ??
        el.querySelector<HTMLElement>('.modal-body input:not([disabled]):not([type=hidden]), .modal-body select, .modal-body textarea') ??
        el.querySelector<HTMLElement>('.modal-foot button') ??
        el.querySelector<HTMLElement>('[data-close]');
      target?.focus();
    }, 10);
    openModals++;
    const app = document.getElementById('root');
    app?.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(timer);
      openModals = Math.max(0, openModals - 1);
      if (openModals === 0) {
        app?.removeAttribute('aria-hidden');
        document.body.style.overflow = '';
      }
      if (prev && document.contains(prev)) prev.focus();
    };
  }, [initialFocus]);

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeRef.current();
      return;
    }
    if (e.key === 'Tab' && box.current) {
      const f = focusables(box.current);
      if (!f.length) return;
      const first = f[0]!;
      const last = f[f.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const handler = (e: KeyboardEvent) => onKeyDown(e);
    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
  });

  return createPortal(
    // Clicar no fundo fecha (atalho de mouse); pelo teclado, Esc e o botão Fechar.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className="scrim-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeRef.current();
      }}
    >
      <div ref={box} className={cx('modal', size)} role="dialog" aria-modal="true" aria-labelledby={id}>
        <div className="modal-head">
          <h2 id={id}>{title}</h2>
          <button type="button" className="icon-btn" data-close onClick={() => closeRef.current()} aria-label={t('ui.close')}>
            <Icon name="x" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {foot && <div className="modal-foot">{foot}</div>}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmOptions {
  title: ReactNode;
  text: ReactNode;
  ok?: string;
  cancel?: string;
  danger?: boolean;
  /** Conteúdo extra (ex.: campo de motivo). */
  extra?: ReactNode;
}

/** Confirmação na própria tela (ações irreversíveis ou que mudam o acesso de alguém). */
export function ConfirmDialog({ o, onAnswer, busy }: { o: ConfirmOptions; onAnswer: (ok: boolean) => void; busy?: boolean }) {
  const { t } = useI18n<CommonKey>();
  return (
    <Modal
      title={o.title}
      size="sm"
      onClose={() => onAnswer(false)}
      initialFocus={o.extra ? undefined : '[data-confirm]'}
      foot={
        <>
          <button type="button" className="btn" onClick={() => onAnswer(false)}>
            {o.cancel ?? t('ui.cancel')}
          </button>
          <button type="button" data-confirm className={cx('btn', o.danger ? 'btn-danger' : 'btn-primary')} disabled={busy} onClick={() => onAnswer(true)}>
            {o.ok ?? t('ui.confirm')}
          </button>
        </>
      }
    >
      <p>{o.text}</p>
      {o.extra}
    </Modal>
  );
}

/** Pergunta de confirmação como Promise: `if (await confirm({...})) …`. */
export function useConfirm(): [(o: ConfirmOptions) => Promise<boolean>, ReactNode] {
  const [state, setState] = useState<{ o: ConfirmOptions; resolve: (v: boolean) => void } | null>(null);
  const ask = useCallback((o: ConfirmOptions) => new Promise<boolean>((resolve) => setState({ o, resolve })), []);
  const node = state ? (
    <ConfirmDialog
      o={state.o}
      onAnswer={(ok) => {
        state.resolve(ok);
        setState(null);
      }}
    />
  ) : null;
  return [ask, node];
}

// ---------------------------------------------------------------- avisos (toasts)

export type ToastKind = 'ok' | 'warn' | 'crit' | 'info';
export interface ToastOptions {
  kind?: ToastKind;
  action?: { label: string; run: () => void };
  /** Em milissegundos. Com ação ("Desfazer") ou de erro, o padrão é 10 s (HU-010), para dar tempo de ler. */
  timeout?: number;
}
interface ToastItem extends ToastOptions {
  id: number;
  msg: ReactNode;
}

const ToastCtx = createContext<((msg: ReactNode, o?: ToastOptions) => void) | null>(null);

/** Região de avisos com aria-live: leitores de tela anunciam cada aviso sem roubar o foco. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useI18n<CommonKey>();
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const remove = useCallback((id: number) => setItems((l) => l.filter((x) => x.id !== id)), []);
  const push = useCallback(
    (msg: ReactNode, o?: ToastOptions) => {
      const id = ++seq.current;
      setItems((l) => [...l.slice(-3), { id, msg, ...o }]);
      window.setTimeout(() => remove(id), o?.timeout ?? (o?.action || o?.kind === 'crit' ? 10000 : 5000));
    },
    [remove],
  );
  return (
    <ToastCtx.Provider value={push}>
      {children}
      {createPortal(
      <div className="toasts" role="status" aria-live="polite" aria-label={t('ui.notifications')}>
        {items.map((x) => (
          <div key={x.id} className={cx('toast', x.kind)}>
            <span className="tmark" aria-hidden="true" />
            <div className="grow">{x.msg}</div>
            {x.action && (
              <button
                type="button"
                onClick={() => {
                  x.action?.run();
                  remove(x.id);
                }}
              >
                {x.action.label}
              </button>
            )}
            <button type="button" aria-label={t('ui.dismiss')} onClick={() => remove(x.id)} style={{ border: 0, padding: 0 }}>
              <Icon name="x" size="sm" />
            </button>
          </div>
        ))}
      </div>,
      document.body,
      )}
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const push = useContext(ToastCtx);
  if (!push) throw new Error('useToast fora do ToastProvider');
  return push;
}

// ---------------------------------------------------------------- abas

export interface TabItem {
  id: string;
  label: ReactNode;
  count?: number | string;
}

/** Abas com teclado (setas, Home, End) e tabindex itinerante, como no protótipo. */
export function Tabs({
  label,
  items,
  current,
  onChange,
  idBase,
}: {
  label: string;
  items: TabItem[];
  current: string;
  onChange: (id: string) => void;
  idBase: string;
}) {
  // aria-controls só aponta para um painel que existe: telas do layout A+C usam as abas como visões, sem TabPanel
  // (um id inexistente reprova o axe em aria-valid-attr-value)
  const [hasPanel, setHasPanel] = useState(false);
  useEffect(() => {
    setHasPanel(!!document.getElementById(`panel-${idBase}`));
  }, [idBase, current]);
  // a linha de abas rola de lado: a aba ativa nunca fica escondida
  useEffect(() => {
    document.getElementById(`tab-${idBase}-${current}`)?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [idBase, current]);
  const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    let i = items.findIndex((x) => x.id === current);
    if (e.key === 'Home') i = 0;
    else if (e.key === 'End') i = items.length - 1;
    else i = (i + (e.key === 'ArrowRight' ? 1 : -1) + items.length) % items.length;
    const next = items[i];
    if (!next) return;
    onChange(next.id);
    window.setTimeout(() => document.getElementById(`tab-${idBase}-${next.id}`)?.focus(), 0);
  };
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {items.map((x) => (
        <button
          key={x.id}
          type="button"
          className="tab"
          role="tab"
          id={`tab-${idBase}-${x.id}`}
          aria-selected={x.id === current}
          aria-controls={hasPanel ? `panel-${idBase}` : undefined}
          tabIndex={x.id === current ? 0 : -1}
          onClick={() => onChange(x.id)}
          onKeyDown={onKeyDown}
        >
          {x.label}
          {x.count != null && <span className="cnt">{x.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function TabPanel({ idBase, current, children }: { idBase: string; current: string; children: ReactNode }) {
  return (
    <div className="tabpanel" role="tabpanel" id={`panel-${idBase}`} aria-labelledby={`tab-${idBase}-${current}`} tabIndex={0}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- menu suspenso

/**
 * Botão que abre um menu (role=menu) posicionado abaixo dele; setas navegam,
 * Esc fecha e devolve o foco ao botão, clique fora fecha.
 */
export function MenuButton({
  button,
  label,
  className,
  children,
  menuLabel,
}: {
  button: ReactNode;
  label?: string;
  className?: string;
  menuLabel: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const close = useCallback((restore?: boolean) => {
    setOpen(false);
    if (restore) btn.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const r = btn.current?.getBoundingClientRect();
      const w = pop.current?.offsetWidth ?? 260;
      if (!r) return;
      const vw = document.documentElement.clientWidth;
      const left = Math.max(8, Math.min(r.right - w, vw - w - 8)) + window.scrollX;
      setPos({ top: r.bottom + 6 + window.scrollY, left });
    };
    place();
    const first = window.setTimeout(() => {
      place();
      pop.current?.querySelector<HTMLElement>('button, a, select, input')?.focus();
    }, 0);
    const onDown = (e: MouseEvent) => {
      if (pop.current?.contains(e.target as Node) || btn.current?.contains(e.target as Node)) return;
      close();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('resize', place);
    return () => {
      window.clearTimeout(first);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', place);
    };
  }, [open, close]);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close(true);
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      const items = Array.from(pop.current?.querySelectorAll<HTMLElement>('.pop-item') ?? []);
      if (!items.length) return;
      e.preventDefault();
      let i = items.indexOf(document.activeElement as HTMLElement);
      i = (i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
      items[i]?.focus();
    }
  };

  const menu = useMemo(() => (open ? children(() => close(true)) : null), [open, children, close]);

  return (
    <>
      <button
        ref={btn}
        type="button"
        className={className ?? 'icon-btn'}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((v) => !v)}
      >
        {button}
      </button>
      {open &&
        createPortal(
          <div
            ref={pop}
            className="pop"
            role="menu"
            tabIndex={-1}
            aria-label={menuLabel}
            style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999 }}
            onKeyDown={onKeyDown}
          >
            {menu}
          </div>,
          document.body,
        )}
    </>
  );
}
